import { useMemo, useCallback } from "react";
import { MassMailModal } from "screens/mass-mail";
import { EmailSendData } from "screens/mass-mail/types";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CandidateInfo } from "../types";
import { convertCandidateToContact, convertCandidatesToContacts } from "./candidateToContact";
import { CANDIDATE_EMAIL_TEMPLATES } from "./candidateEmailTemplates";

/**
 * Determine if a candidate is an Invited entity (vs an Application).
 * The backend `isInvited` flag is the source of truth — derived statuses like
 * "reminder-sent" and "forwarded-to-broker" exist in BOTH the Invited and
 * Application tables, so status alone is ambiguous. Fall back to status only for
 * legacy/cached rows that predate the flag.
 * This distinction matters: the backend uses different tables and different
 * side-effects (delete vs archive) depending on this flag.
 */
function isInvitedCandidate(candidate: CandidateInfo | undefined): boolean {
  if (!candidate) return false;
  if (typeof candidate.isInvited === "boolean") return candidate.isInvited;
  const s = candidate.status;
  return s === "notify" || s === "invited" || s === "reminder-sent";
}

type CandidateTemplateType =
  | "invite"
  | "invite-reminder"
  | "rejection"
  | "archive"
  | "archive-noresponse"
  | "broker-forward"
  | "broker-contact";

/**
 * Backend-accepted template types. Frontend-only variants (e.g.
 * invite-reminder, archive-noresponse) map to their base type so they reuse
 * the same backend flow (invite placeholder resolution / archive side effects).
 */
type BackendTemplateType =
  | "invite"
  | "invite-reminder"
  | "rejection"
  | "archive"
  | "broker-forward"
  | "broker-contact";

function toBackendTemplateType(type: CandidateTemplateType): BackendTemplateType {
  if (type === "archive-noresponse") return "archive";
  return type;
}

/**
 * Determine which candidate template is active based on email subject.
 * Compares using the static prefix before {unitId} to handle cases where
 * the placeholder has already been replaced with the actual unit ID.
 * Falls back to "rejection" if no match is found.
 */
function detectTemplateType(subject: string): CandidateTemplateType {
  for (const template of CANDIDATE_EMAIL_TEMPLATES) {
    // Compare the static prefix before any placeholder
    const prefix = template.subject.split("{")[0];
    if (prefix && subject.startsWith(prefix)) {
      return template.id as CandidateTemplateType;
    }
  }
  return "rejection";
}

interface BrokerContactInfo {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone_mobile?: string;
  company?: { id?: number; name?: string } | null;
}

interface CandidateEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Single candidate */
  candidate?: CandidateInfo | null;
  /** Bulk candidates */
  candidates?: CandidateInfo[];
  /** Template type to pre-select: 'rejection' | 'archive' | 'invite'. Omit for blank. */
  defaultTemplateType?: string;
  /** Brokers linked to the candidate's unit — enables broker-related templates */
  brokers?: BrokerContactInfo[] | null;
  /** Full postal address of the candidate's unit building (googlePlaceLongAddress) */
  buildingAddress?: string | null;
}

export const CandidateEmailModal = ({
  isOpen,
  onClose,
  onSuccess,
  candidate,
  candidates,
  defaultTemplateType,
  brokers,
  buildingAddress,
}: CandidateEmailModalProps) => {
  const { session } = useSession();

  // Normalize + filter brokers we can actually email/display.
  const brokerList = useMemo(
    () =>
      (brokers ?? []).filter(
        (b) =>
          !!b &&
          (b.email ||
            b.phone_mobile ||
            `${b.firstName || ""} ${b.lastName || ""}`.trim()),
      ),
    [brokers],
  );
  const hasBrokers = brokerList.length > 0;

  // HTML-ready broker block used in the broker-contact template so that one
  // template line expands into the full list of brokers for the candidate.
  const brokerListHtml = useMemo(() => {
    if (!hasBrokers) return "";
    return brokerList
      .map((b) => {
        const fullName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
        const company = b.company?.name ? ` (${b.company.name})` : "";
        const email = b.email
          ? `<strong>E-mail:</strong> <a href="mailto:${b.email}">${b.email}</a>`
          : "";
        const phone = b.phone_mobile ? `<strong>Telefoon:</strong> ${b.phone_mobile}` : "";
        const contactLine = [email, phone].filter(Boolean).join(" · ");
        return `<li><strong>${fullName}</strong>${company}${contactLine ? `<br/>${contactLine}` : ""}</li>`;
      })
      .join("");
  }, [brokerList, hasBrokers]);

  // Aggregate display helpers for broker placeholders used in the shared
  // template (e.g. the subject and greeting).
  const brokerFullNameList = useMemo(
    () =>
      brokerList
        .map((b) => `${b.firstName || ""} ${b.lastName || ""}`.trim())
        .filter(Boolean)
        .join(", "),
    [brokerList],
  );
  const brokerEmailList = useMemo(
    () => brokerList.map((b) => b.email).filter(Boolean).join(", "),
    [brokerList],
  );
  const brokerPhoneList = useMemo(
    () => brokerList.map((b) => b.phone_mobile).filter(Boolean).join(", "),
    [brokerList],
  );

  // Filter out broker templates when no broker is linked — they'd render
  // empty placeholders and the recipient override would be missing.
  const availableTemplates = useMemo(() => {
    if (hasBrokers) return CANDIDATE_EMAIL_TEMPLATES;
    return CANDIDATE_EMAIL_TEMPLATES.filter(
      (t) => t.id !== "broker-forward" && t.id !== "broker-contact",
    );
  }, [hasBrokers]);

  // For single candidate: auto-select invite template when status is "notify"
  const resolvedTemplateType = useMemo(() => {
    if (defaultTemplateType) return defaultTemplateType;
    if (candidate && candidate.status === "notify") return "invite";
    return undefined; // blank
  }, [defaultTemplateType, candidate?.status]);

  const initialState = useMemo(() => {
    // Build contacts list
    let contacts: import("screens/mass-mail/types").Contact[];
    if (candidates && candidates.length > 0) {
      contacts = convertCandidatesToContacts(candidates);
    } else if (candidate) {
      const c = convertCandidateToContact(candidate);
      contacts = c.email ? [c] : [];
    } else {
      contacts = [];
    }

    // Determine unit for context placeholder (single candidate)
    const unitId = candidate?.unitId || "";

    // Build broker "contacts" for the doorsturen-naar-makelaar recipient swap.
    // Each linked broker with an email becomes its own MassMail contact so the
    // email goes to every broker simultaneously.
    const brokerMassMailContacts: import("screens/mass-mail/types").Contact[] =
      brokerList
        .filter((b) => !!b.email)
        .map((b) => {
          const fullName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
          return {
            id: `broker-${b.id ?? b.email}`,
            name: fullName || b.email || "Makelaar",
            resourceId: b.id ? String(b.id) : b.email || "",
            firstName: b.firstName || "",
            lastName: b.lastName || "",
            email: b.email,
            phoneMobile: b.phone_mobile || "",
            units: [],
            unitId,
            buildingId: candidate?.buildingId || "",
          };
        });

    // Pre-select template if a default is resolved, otherwise start blank
    const defaultTemplate = resolvedTemplateType
      ? availableTemplates.find((t) => t.id === resolvedTemplateType)
      : undefined;

    // If the pre-selected template is the broker forward, start with the full
    // broker list as recipients instead of the candidate.
    const effectiveContacts =
      defaultTemplate?.id === "broker-forward" && brokerMassMailContacts.length > 0
        ? brokerMassMailContacts
        : contacts;

    return {
      contacts: effectiveContacts,
      // Baseline used by the reducer to restore the candidate as recipient when
      // the user switches away from a broker-forward-style template.
      defaultContacts: contacts,
      // Template-specific recipient overrides consumed by the MassMail reducer.
      templateContactOverrides: brokerMassMailContacts.length > 0
        ? { "broker-forward": brokerMassMailContacts }
        : undefined,
      emailSubject: defaultTemplate?.subject || "",
      emailContent: defaultTemplate?.content || "",
      // Set selectedTemplate + selectedType + filteredTemplates so the
      // TemplateManager shows the description banner immediately on open
      ...(defaultTemplate
        ? {
            selectedTemplate: String(defaultTemplate.id),
            selectedType: defaultTemplate.templateType,
            filteredTemplates: availableTemplates.filter(
              (t) => t.templateType === defaultTemplate.templateType,
            ),
          }
        : {}),
      contextPlaceholders: {
        unitId,
        // Broker placeholders aggregate across the full linked list (comma-
        // separated). Works for single broker and multi-broker setups alike.
        "broker.fullName": brokerFullNameList,
        "broker.email": brokerEmailList,
        "broker.phone": brokerPhoneList,
        // {brokers.html} is a ready-to-inject list block for templates aimed at
        // candidates (broker-contact), so one line expands into the full set
        // of linked makelaars with emails + phones.
        "brokers.html": brokerListHtml,
        "building.address": buildingAddress || "",
        "building.googlePlaceLongAddress": buildingAddress || "",
        // Keep candidate data available even when the recipient is swapped to
        // the broker (broker-forward template). {contact.*} will resolve to the
        // broker in that case, so the template uses {candidate.*} for the
        // candidate info it wants to forward.
        "candidate.fullName": candidate
          ? `${candidate.personalDetails?.firstName || ""} ${candidate.personalDetails?.lastName || ""}`.trim()
          : "",
        "candidate.email": candidate?.personalDetails?.email || "",
        "candidate.phone": candidate?.personalDetails?.phoneNumber || "",
      },
    };
  }, [
    candidate?.id,
    candidate?.buildingId,
    candidate?.unitId,
    candidates,
    resolvedTemplateType,
    availableTemplates,
    brokerList,
    brokerFullNameList,
    brokerEmailList,
    brokerPhoneList,
    brokerListHtml,
    buildingAddress,
    candidate?.personalDetails?.firstName,
    candidate?.personalDetails?.lastName,
    candidate?.personalDetails?.email,
    candidate?.personalDetails?.phoneNumber,
  ]);

  // Build a lookup map of isInvited by candidate ID so we can route archive vs
  // delete side-effects per contact. Uses the backend isInvited flag (with a
  // status fallback) rather than raw status, which is ambiguous across tables.
  const candidateIsInvitedMap = useMemo(() => {
    const map = new Map<string, boolean>();
    if (candidate) {
      map.set(String(candidate.id), isInvitedCandidate(candidate));
    }
    if (candidates) {
      for (const c of candidates) {
        map.set(String(c.id), isInvitedCandidate(c));
      }
    }
    return map;
  }, [candidate, candidates]);

  const handleSend = useCallback(
    async (emailData: EmailSendData) => {
      const results: Array<{ contactId: string; contactName: string; email: string; status: "success" | "failed"; error?: string }> = [];

      // Detect the selected template once for the whole send. Broker templates
      // never archive and can be sent to arbitrary recipients (broker, user
      // themselves, external party), so they don't require contact.id to be a
      // candidate ID. Legacy templates (rejection/invite/archive) still rely on
      // contact.id being a candidate ID — that's the original MassMail flow.
      const templateType = detectTemplateType(emailData.subject);
      const isBrokerTemplate =
        templateType === "broker-forward" || templateType === "broker-contact";

      // Original candidate the modal was opened for — used as the data-lookup
      // anchor for broker templates, regardless of which recipients the user
      // added via the resource selector (those have contact IDs, not candidate
      // IDs, and would fail the candidate lookup on the backend).
      const anchorCandidateId = candidate?.id ? String(candidate.id) : undefined;
      const anchorUnitId = candidate?.unitId || "";

      // Build Graph-shaped CC/BCC arrays once. The same set is forwarded with
      // every send in the loop — typical use is a single broker (or candidate)
      // recipient with one CC, so duplication isn't a concern. The backend
      // dedupes any CC/BCC entry that matches the primary recipient.
      const toGraphRecipients = (
        contacts: Array<{ name: string; email: string }> | undefined,
      ) =>
        (contacts || [])
          .filter((c) => c.email && c.email.trim())
          .map((c) => ({
            emailAddress: { address: c.email.trim(), name: c.name || c.email.trim() },
          }));
      const ccRecipients = toGraphRecipients(emailData.ccContacts);
      const bccRecipients = toGraphRecipients(emailData.bccContacts);

      for (const contact of emailData.contacts) {
        const contactLabelId = contact.id;

        // For broker templates, anchor the backend lookup to the original
        // candidate; for legacy templates keep the existing per-contact flow.
        const candidateIdToUse = isBrokerTemplate
          ? anchorCandidateId
          : contact.id;

        if (!candidateIdToUse) {
          results.push({
            contactId: contactLabelId,
            contactName: contact.name,
            email: contact.email || "",
            status: "failed",
            error: "Geen gekoppelde kandidaat gevonden",
          });
          continue;
        }

        // isInvited only matters for legacy templates (rejection/invite/archive)
        // which drive archiving behavior. Broker templates don't archive, so the
        // hint is irrelevant — but pass something sensible anyway.
        const isInvited = isBrokerTemplate
          ? candidateIsInvitedMap.get(candidateIdToUse) ?? false
          : candidateIsInvitedMap.get(contact.id) ?? false;

        // Replace placeholders the backend doesn't resolve. For broker templates
        // use the anchor candidate's unitId so all extra recipients see the
        // right unit; for legacy templates keep per-contact replacement.
        const unitIdForReplace = isBrokerTemplate
          ? anchorUnitId
          : contact.unitId || "";
        const candidateFullName = candidate
          ? `${candidate.personalDetails?.firstName || ""} ${candidate.personalDetails?.lastName || ""}`.trim()
          : "";
        // For broker-forward we address the currently-iterated broker directly
        // (one mail per broker so each sees "Beste <their name>"). For other
        // templates {broker.*} aggregates across all linked brokers.
        const isBrokerForwardThisLoop = templateType === "broker-forward";
        const brokerFullName = isBrokerForwardThisLoop
          ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim()
          : brokerFullNameList;
        const brokerEmail = isBrokerForwardThisLoop
          ? contact.email || ""
          : brokerEmailList;
        const brokerPhone = isBrokerForwardThisLoop
          ? contact.phoneMobile || ""
          : brokerPhoneList;

        const applyPlaceholders = (text: string) =>
          text
            .replace(/\{unitId\}/g, unitIdForReplace)
            .replace(/\{broker\.fullName\}/g, brokerFullName)
            .replace(/\{broker\.email\}/g, brokerEmail)
            .replace(/\{broker\.phone\}/g, brokerPhone)
            .replace(/\{brokers\.html\}/g, brokerListHtml || "")
            .replace(/\{building\.address\}/g, buildingAddress || "")
            .replace(/\{building\.googlePlaceLongAddress\}/g, buildingAddress || "")
            .replace(/\{candidate\.fullName\}/g, candidateFullName)
            .replace(/\{candidate\.email\}/g, candidate?.personalDetails?.email || "")
            .replace(/\{candidate\.phone\}/g, candidate?.personalDetails?.phoneNumber || "");

        const processedContent = applyPlaceholders(emailData.content);
        const processedSubject = applyPlaceholders(emailData.subject);

        // Determine the recipient email address:
        // - broker-forward: the iterated contact IS a broker (recipient swap).
        //   One mail is sent per broker so each gets addressed personally.
        // - broker-contact: always send to the iterated contact's email
        //   (candidate by default, extra recipients get copies).
        // - legacy templates: backend sends to the candidate, no override needed.
        let recipientEmailOverride: string | undefined;
        if (templateType === "broker-forward") {
          recipientEmailOverride =
            contact.email?.trim() || brokerList[0]?.email || undefined;
        } else if (templateType === "broker-contact") {
          recipientEmailOverride = contact.email?.trim() || undefined;
        }

        if (isBrokerTemplate && !recipientEmailOverride) {
          results.push({
            contactId: contactLabelId,
            contactName: contact.name,
            email: contact.email || "",
            status: "failed",
            error:
              templateType === "broker-forward"
                ? "Geen e-mailadres voor makelaar of extra ontvanger"
                : "Geen e-mailadres voor ontvanger",
          });
          Sentry.captureMessage("Broker email skipped: no recipient email available", {
            level: "warning",
            tags: { feature: "candidateEmail", method: "handleSend" },
            extra: { templateType, anchorCandidateId, contactId: contactLabelId },
          });
          continue;
        }

        try {
          const response = await fetchWithAuth(
            `${API_URL}/rental-appointment/send-custom-email/${candidateIdToUse}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                templateType: toBackendTemplateType(templateType),
                customContent: processedContent,
                customSubject: processedSubject,
                isInvited,
                recipientEmailOverride,
                ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
                bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
              }),
            },
            session,
          );

          if (response.ok) {
            results.push({
              contactId: contact.id,
              contactName: contact.name,
              email: contact.email || "",
              status: "success",
            });
          } else {
            results.push({
              contactId: contact.id,
              contactName: contact.name,
              email: contact.email || "",
              status: "failed",
              error: `HTTP ${response.status}`,
            });
            Sentry.captureException(new Error("Candidate email send failed"), {
              extra: {
                candidateIdUsed: candidateIdToUse,
                contactId: contactLabelId,
                status: response.status,
                templateType,
              },
              tags: { feature: "candidateEmail", method: "handleSend" },
            });
          }
        } catch (error) {
          results.push({
            contactId: contactLabelId,
            contactName: contact.name,
            email: contact.email || "",
            status: "failed",
            error: error instanceof Error ? error.message : "Unknown error",
          });
          Sentry.captureException(error, {
            tags: { feature: "candidateEmail", method: "handleSend" },
            extra: {
              candidateIdUsed: candidateIdToUse,
              contactId: contactLabelId,
              templateType,
            },
          });
        }
      }

      const successCount = results.filter((r) => r.status === "success").length;
      const failCount = results.filter((r) => r.status === "failed").length;

      if (failCount === 0) {
        toast.success(`${successCount} e-mail(s) succesvol verstuurd!`);
      } else if (successCount > 0) {
        toast.warn(`${successCount} verstuurd, ${failCount} mislukt`);
      } else {
        toast.error(`Alle ${failCount} e-mails mislukt`);
      }

      return {
        totalSent: successCount,
        totalFailed: failCount,
        totalContacts: results.length,
        timestamp: new Date().toISOString(),
        senderEmail: emailData.senderEmail,
        subject: emailData.subject,
        results,
      };
    },
    [
      session,
      candidateIsInvitedMap,
      brokerList,
      brokerFullNameList,
      brokerEmailList,
      brokerPhoneList,
      brokerListHtml,
      buildingAddress,
      candidate?.id,
      candidate?.unitId,
      candidate?.personalDetails?.firstName,
      candidate?.personalDetails?.lastName,
      candidate?.personalDetails?.email,
      candidate?.personalDetails?.phoneNumber,
    ],
  );

  if (!isOpen) return null;

  return (
    <MassMailModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      onSend={handleSend}
      contextTemplates={availableTemplates}
      initialState={initialState}
    />
  );
};
